import { Link } from "react-router-dom";
import useSWR from "swr";
import { ArrowRight, Layers, Youtube } from "lucide-react";
import { listCollections, formatNumber } from "@/lib/api";
import usePageMeta from "@/hooks/use-page-meta";
import usePageJsonLd from "@/hooks/use-page-jsonld";
import { LevelJumpBox } from "@/components/LevelJumpBox";

const LEVEL_PREFIX_RE = /^Pet Rescue Saga level\s+(\d+)\s*-\s*(\d+)/i;

export default function PetRescueSaga() {
  const { data, isLoading } = useSWR("/collections", () => listCollections());

  const all = data?.items || [];

  const levelPlaylists = all
    .map((c) => {
      const m = c.title && c.title.match(LEVEL_PREFIX_RE);
      if (!m) return null;
      return { ...c, _from: parseInt(m[1], 10), _to: parseInt(m[2], 10) };
    })
    .filter(Boolean)
    .sort((a, b) => a._from - b._from);

  const extras = all.filter(
    (c) =>
      /^Pet Rescue Saga/i.test(c.title || "") &&
      !LEVEL_PREFIX_RE.test(c.title || "")
  );

  const totalVideos = levelPlaylists.reduce((n, p) => n + (p.count || 0), 0);
  const highestLevel = levelPlaylists.length
    ? levelPlaylists[levelPlaylists.length - 1]._to
    : 0;

  usePageMeta({
    title: "Pet Rescue Saga — Every Level Playlist",
    description: `${levelPlaylists.length} Pet Rescue Saga playlists covering levels 21 through ${highestLevel.toLocaleString()} — ${totalVideos.toLocaleString()} walkthroughs from the tobiasdeamon Vault.`,
    type: "website",
  });

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  usePageJsonLd({
    key: "prs-collection",
    data:
      origin && levelPlaylists.length
        ? {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Pet Rescue Saga — Every Level Playlist",
            description: `${levelPlaylists.length} Pet Rescue Saga playlists covering levels 21 through ${highestLevel} — ${totalVideos} walkthroughs.`,
            url: `${origin}/pet-rescue-saga`,
            mainEntity: {
              "@type": "ItemList",
              numberOfItems: levelPlaylists.length,
              itemListElement: levelPlaylists.map((p, i) => ({
                "@type": "ListItem",
                position: i + 1,
                url: `${origin}/collections/${p.slug}`,
                name: p.title,
              })),
            },
          }
        : null,
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="prs-page">
      {/* Hero */}
      <div className="mb-10 sm:mb-14">
        <div className="mono-label">The Saga shelf</div>
        <h1 className="font-serif-editorial text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#f4f1ea] mt-3">
          Pet Rescue Saga <span className="italic text-[#d95d39]">—</span> every level.
        </h1>
        <p
          data-testid="prs-lede"
          className="mt-4 sm:mt-5 max-w-2xl text-base sm:text-lg text-white/60 leading-relaxed"
        >
          A decade of Pet Rescue Saga runs, grouped the way YouTube organises them —
          one playlist per 100-level block, from the earliest attempts to the latest
          releases. {highestLevel ? `Levels 21 through ${highestLevel.toLocaleString()}.` : ""}
        </p>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-3 gap-3 sm:gap-6 max-w-xl">
          <Stat label="Playlists" value={formatNumber(levelPlaylists.length)} />
          <Stat label="Levels covered" value={highestLevel ? `21–${highestLevel.toLocaleString()}` : "—"} />
          <Stat label="Walkthroughs" value={formatNumber(totalVideos)} />
        </div>

        <LevelJumpBox maxLevel={highestLevel} />
      </div>

      {/* Grid of level playlists */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} className="aspect-[16/10] rounded-xl bg-white/[0.03] animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5"
            data-testid="prs-levels-grid"
          >
            {levelPlaylists.map((p) => (
              <LevelCard key={p.slug} p={p} />
            ))}
          </div>

          {extras.length > 0 && (
            <section className="mt-16 pt-10 border-t border-white/10">
              <div className="mono-label text-white/50">Also in the Saga</div>
              <h2 className="font-serif-editorial text-3xl sm:text-4xl text-[#f4f1ea] mt-3">
                Side playlists.
              </h2>
              <div
                className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5"
                data-testid="prs-extras-grid"
              >
                {extras.map((p) => (
                  <LevelCard key={p.slug} p={p} />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-white/10 bg-[#12141d] px-4 py-3">
      <div className="font-serif-editorial text-2xl sm:text-3xl text-[#f4f1ea]">{value}</div>
      <div className="mono-label text-white/40 mt-1">{label}</div>
    </div>
  );
}

function LevelCard({ p }) {
  return (
    <Link
      to={`/collections/${p.slug}`}
      data-testid={`prs-level-card-${p.slug}`}
      className="group hover-lift block rounded-xl border border-white/10 bg-[#12141d] overflow-hidden"
    >
      <Cover thumbnails={p.preview_thumbnails} />
      <div className="p-4 sm:p-5">
        <div className="flex items-center justify-between">
          <span className="mono-label text-white/40">{formatNumber(p.count)} videos</span>
          {p.source === "youtube" && (
            <span
              title="Imported from YouTube"
              className="mono-label text-[#d95d39] inline-flex items-center gap-1"
            >
              <Youtube className="w-3 h-3" /> YT
            </span>
          )}
          <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-[#d95d39] group-hover:translate-x-1 transition-all" />
        </div>
        <h3 className="mt-2 font-serif-editorial text-xl sm:text-2xl text-[#f4f1ea] group-hover:text-[#d95d39] transition-colors line-clamp-2">
          {p.title}
        </h3>
      </div>
    </Link>
  );
}

function Cover({ thumbnails = [] }) {
  if (!thumbnails.length) {
    return (
      <div className="aspect-video bg-gradient-to-br from-[#1a1d29] to-[#0c0e15] flex items-center justify-center">
        <Layers className="w-7 h-7 text-white/20" />
      </div>
    );
  }
  if (thumbnails.length === 1) {
    return (
      <div className="aspect-video bg-black">
        <img
          src={thumbnails[0]}
          alt=""
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover"
        />
      </div>
    );
  }
  const grid = thumbnails.slice(0, 4);
  return (
    <div className="aspect-video grid grid-cols-2 gap-0.5 bg-black">
      {grid.map((t, i) => (
        <div key={i} className="overflow-hidden">
          <img
            src={t}
            alt=""
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover"
          />
        </div>
      ))}
    </div>
  );
}
